import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import useAxiosAuth from "../hooks/useAxiosAuth";

// Tenant management (super_admin only) — the hospitals/clinics running on the
// shared multi-tenant server. Mirrors adminSlice.js in shape so the Tenants and
// Admins pages behave the same way.
//
// Backend: /api/tenants (see api/routes/tenantManagementRoutes.js).
// There is deliberately no delete thunk — a tenant holds clinical data, so
// setTenantStatus('inactive') is how one is taken offline.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

// Paginated list for the Tenant Management page.
export const selectTenantList = createAsyncThunk(
    'tenant/selectTenantList',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', status = '', countryId = [], page = 1, limit = 10 } =
                typeof params === 'string' ? { search: params } : params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (status) qs.append('status', status);
            if (countryId?.length) qs.append('countryId', countryId.join(','));
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/tenants?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Lightweight id+name list for the tenant picker on Add Admin / Add Doctor.
export const getTenantOptions = createAsyncThunk(
    'tenant/getTenantOptions',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get('/tenants/options');
            return response.data?.tenants || [];
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const getTenantById = createAsyncThunk(
    'tenant/getTenantById',
    async (tenantId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/tenants/${tenantId}`);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const createTenant = createAsyncThunk(
    'tenant/createTenant',
    async (payload, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post('/tenants', payload);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// countryId is fixed at creation — the API rejects a change outright, so the
// edit form shows it read-only rather than letting the user discover that.
export const updateTenant = createAsyncThunk(
    'tenant/updateTenant',
    async ({ tenantId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/tenants/${tenantId}`, data);
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// The hospital's patient-code prefix (APO001 …), super_admin override: sets
// it where the tenant admin hasn't, and may CHANGE a locked one — the server
// then renames every code keeping its number (APO034 -> SUN034). Its own
// endpoint, not an updateTenant field, because the save has those side
// effects (tenantManagementController.setTenantPatientCodePrefix).
export const setTenantPatientCodePrefix = createAsyncThunk(
    'tenant/setTenantPatientCodePrefix',
    async ({ tenantId, prefix }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/tenants/${tenantId}/patient-code`, { prefix });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Deactivating blocks login for every doctor and admin in the tenant.
export const setTenantStatus = createAsyncThunk(
    'tenant/setTenantStatus',
    async ({ tenantId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/tenants/${tenantId}/status`, { status });
            return response.data?.data || response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// The MOC open-platform flag (tenantModel.js — "exactly one tenant normally
// has it true"). Setting it true on one tenant unsets it on any other, server
// side (tenantManagementController.setOpenPlatform) — this thunk just carries
// the intent, not the invariant.
export const setOpenPlatform = createAsyncThunk(
    'tenant/setOpenPlatform',
    async ({ tenantId, isOpenPlatform }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/tenants/${tenantId}/open-platform`, { isOpenPlatform });
            return { tenantId, isOpenPlatform: response.data?.isOpenPlatform };
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ---------------------------------------------------------------------------
// Per-tenant object storage (super_admin)
// ---------------------------------------------------------------------------
// The API never returns credentials — only a masked hint — so the form shows a
// placeholder for an already-configured key and only sends a new secret when
// one is actually typed. Sending back the masked value would overwrite the real
// credential with asterisks.

export const getTenantStorage = createAsyncThunk(
    'tenant/getTenantStorage',
    async (tenantId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/tenants/${tenantId}/storage`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const saveTenantStorage = createAsyncThunk(
    'tenant/saveTenantStorage',
    async ({ tenantId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/tenants/${tenantId}/storage`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Re-probe the saved configuration without changing it.
export const testTenantStorage = createAsyncThunk(
    'tenant/testTenantStorage',
    async (tenantId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.post(`/tenants/${tenantId}/storage/test`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ---------------------------------------------------------------------------
// Feature access control (super_admin)
// ---------------------------------------------------------------------------
// The catalogue comes from the API rather than a local copy, so the list can't
// drift out of sync with what the backend actually enforces.

export const getFeatureCatalogue = createAsyncThunk(
    'tenant/getFeatureCatalogue',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get('/tenants/feature-catalogue');
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const getTenantFeatures = createAsyncThunk(
    'tenant/getTenantFeatures',
    async (tenantId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/tenants/${tenantId}/features`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const saveTenantFeatures = createAsyncThunk(
    'tenant/saveTenantFeatures',
    // `services` — which of the six services the tenant bought (India-SaaS
    // restructure). Sent alongside features; the server gates features by it.
    //
    // `planPricing` rides in the SAME body rather than getting its own call:
    // the price is a consequence of the ticked features, so saving one without
    // the other would leave a tenant priced for a plan it no longer has. The
    // server recomputes the finals from subtotal + discount before storing.
    async ({ tenantId, features, settings, services, planPricing }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const body = { features, settings, services };
            if (planPricing) body.planPricing = planPricing;
            const response = await axiosInstance.put(`/tenants/${tenantId}/features`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ---------------------------------------------------------------------------
// Feature price list (super_admin ONLY)
// ---------------------------------------------------------------------------
// What each feature costs per month / per year, in INR. This is internal
// commercial data: it must never reach a tenant admin, so it lives only behind
// this super-admin endpoint and is rendered only on super-admin surfaces.
export const getFeaturePrices = createAsyncThunk(
    'tenant/getFeaturePrices',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get('/feature-prices');
            return response.data?.prices || [];
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// ---------------------------------------------------------------------------
// Maintenance account (super_admin)
// ---------------------------------------------------------------------------
// A real admin login inside a tenant for the platform maintenance team. One per
// tenant, optional. The temp password comes back once on creation and is never
// retrievable again.

export const getMaintenanceAdmin = createAsyncThunk(
    'tenant/getMaintenanceAdmin',
    async (tenantId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.get(`/tenants/${tenantId}/maintenance`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const saveMaintenanceAdmin = createAsyncThunk(
    'tenant/saveMaintenanceAdmin',
    async ({ tenantId, data }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.put(`/tenants/${tenantId}/maintenance`, data);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

export const setMaintenanceAdminStatus = createAsyncThunk(
    'tenant/setMaintenanceAdminStatus',
    async ({ tenantId, status }, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const response = await axiosInstance.patch(`/tenants/${tenantId}/maintenance/status`, { status });
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

// Every maintenance account across every tenant, with tenant names attached.
export const listMaintenanceAdmins = createAsyncThunk(
    'tenant/listMaintenanceAdmins',
    async (params = {}, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const { search = '', tenantId = '', status = '', page = 1, limit = 20 } = params;
            const qs = new URLSearchParams();
            if (search) qs.append('search', search);
            if (tenantId) qs.append('tenantId', tenantId);
            if (status) qs.append('status', status);
            qs.append('page', String(page));
            qs.append('limit', String(limit));
            const response = await axiosInstance.get(`/tenants/maintenance-admins?${qs.toString()}`);
            return response.data;
        } catch (error) {
            return rejectWithValue({ error: getSerializableError(error) });
        }
    }
);

const tenantSlice = createSlice({
    name: "tenant",
    initialState: {
        tenant: null,
        tenantList: [],
        tenantOptions: [],
        storage: null,
        storageUsingPlatformDefault: true,
        storageLoading: false,
        storageSaving: false,
        storageTesting: false,
        featureGroups: [],
        coreFeatureIds: [],
        // Services layer (India-SaaS restructure), all from the server catalogue.
        servicesCatalogue: [],      // the six sellable services
        globalCoreIds: [],          // always-on features (dashboard, patients)
        serviceCoreIds: {},         // serviceId -> its locked core feature ids
        featureServiceMap: {},      // featureId -> owning service ('common'/null)
        // Descriptive classification labels ({ id, label, description }),
        // chosen on the form's Details tab. They carry no services and gate
        // nothing — services are ticked one by one on the Services tab.
        tenantTypes: [],
        tenantServices: null,       // the loaded tenant's services map
        tenantMeta: null,           // { connectionMode, subdomain, tenantType, isOpenPlatform, planPricing, supportLevel? }
        // featureId -> { monthlyPrice, yearlyPrice }, INR. Super-admin only.
        featurePrices: [],
        featurePricesLoading: false,
        // Which feature needs which, from the server. The form applies these as
        // you click so a broken combination can't be built.
        featureDependencies: {},
        tenantSettings: [],
        // Settings catalogue with platform defaults (create form; see above).
        settingsCatalogue: [],
        features: null,
        featuresLoading: false,
        featuresSaving: false,
        maintenance: null,
        maintenanceLoading: false,
        maintenanceSaving: false,
        maintenanceList: [],
        maintenanceTotal: 0,
        maintenanceListLoading: false,
        tenantTotal: 0,
        tenantTotalPages: 1,
        tenantListLoading: false,
        mutating: false,
        loading: false,
        error: null,
        success: false,
    },
    reducers: {
        resetTenantState: (state) => {
            state.tenant = null;
            state.error = null;
            state.success = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(selectTenantList.pending, (state) => {
                state.tenantListLoading = true;
                state.error = null;
            })
            .addCase(selectTenantList.fulfilled, (state, action) => {
                state.tenantListLoading = false;
                // Rows are stored whole, never re-mapped onto a known field
                // list — the list page reads `planPricing` (and whatever the
                // API adds next) straight off them.
                state.tenantList = action.payload?.tenants || [];
                state.tenantTotal = action.payload?.total || 0;
                state.tenantTotalPages = action.payload?.totalPages || 1;
            })
            .addCase(selectTenantList.rejected, (state, action) => {
                state.tenantListLoading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : 'Failed to fetch tenants';
            })

            .addCase(getTenantOptions.fulfilled, (state, action) => {
                state.tenantOptions = action.payload || [];
            })

            .addCase(getTenantById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getTenantById.fulfilled, (state, action) => {
                state.loading = false;
                state.tenant = action.payload;
            })
            .addCase(getTenantById.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : 'Failed to load tenant';
            })

            .addCase(createTenant.pending, (state) => {
                state.mutating = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createTenant.fulfilled, (state, action) => {
                state.mutating = false;
                state.tenant = action.payload;
                state.success = true;
            })
            .addCase(createTenant.rejected, (state, action) => {
                state.mutating = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : 'Failed to create tenant';
                state.success = false;
            })

            .addCase(updateTenant.pending, (state) => {
                state.mutating = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateTenant.fulfilled, (state, action) => {
                state.mutating = false;
                state.tenant = action.payload;
                state.success = true;
            })
            .addCase(updateTenant.rejected, (state, action) => {
                state.mutating = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : 'Failed to update tenant';
                state.success = false;
            })

            .addCase(setTenantStatus.pending, (state) => {
                state.mutating = true;
                state.error = null;
            })
            .addCase(setTenantStatus.fulfilled, (state, action) => {
                state.mutating = false;
                const updated = action.payload;
                // Patch in place so the row flips without a full refetch.
                state.tenantList = state.tenantList.map((t) =>
                    t.tenantId === updated?.tenantId ? { ...t, ...updated } : t
                );
            })
            .addCase(setTenantStatus.rejected, (state, action) => {
                state.mutating = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : 'Failed to update tenant status';
            })

            .addCase(setOpenPlatform.pending, (state) => {
                state.mutating = true;
                state.error = null;
            })
            .addCase(setOpenPlatform.fulfilled, (state, action) => {
                state.mutating = false;
                if (state.tenantMeta) state.tenantMeta.isOpenPlatform = action.payload.isOpenPlatform;
            })
            .addCase(setOpenPlatform.rejected, (state, action) => {
                state.mutating = false;
                state.error = typeof action.payload?.error === 'string' ? action.payload.error : 'Failed to update the open-platform flag';
            })

            .addCase(getTenantStorage.pending, (state) => {
                state.storageLoading = true;
                state.storage = null;
            })
            .addCase(getTenantStorage.fulfilled, (state, action) => {
                state.storageLoading = false;
                state.storage = action.payload?.data || null;
                state.storageUsingPlatformDefault = action.payload?.usingPlatformDefault !== false;
            })
            .addCase(getTenantStorage.rejected, (state) => {
                state.storageLoading = false;
                state.storage = null;
            })

            .addCase(saveTenantStorage.pending, (state) => {
                state.storageSaving = true;
            })
            .addCase(saveTenantStorage.fulfilled, (state, action) => {
                state.storageSaving = false;
                state.storage = action.payload?.data || state.storage;
                // A config that failed its connection probe is saved inactive,
                // so the tenant is still on the platform bucket.
                state.storageUsingPlatformDefault = !(action.payload?.data?.isActive);
            })
            .addCase(saveTenantStorage.rejected, (state) => {
                state.storageSaving = false;
            })

            .addCase(testTenantStorage.pending, (state) => {
                state.storageTesting = true;
            })
            .addCase(testTenantStorage.fulfilled, (state, action) => {
                state.storageTesting = false;
                state.storage = action.payload?.data || state.storage;
            })
            .addCase(testTenantStorage.rejected, (state) => {
                state.storageTesting = false;
            })

            .addCase(getFeatureCatalogue.fulfilled, (state, action) => {
                state.featureGroups = action.payload?.groups || [];
                state.coreFeatureIds = action.payload?.coreFeatureIds || [];
                state.featureDependencies = action.payload?.dependencies || {};
                state.servicesCatalogue = action.payload?.services || [];
                state.globalCoreIds = action.payload?.globalCoreIds || [];
                state.serviceCoreIds = action.payload?.serviceCoreIds || {};
                state.featureServiceMap = action.payload?.featureServiceMap || {};
                state.tenantTypes = action.payload?.tenantTypes || [];
                // The settings catalogue with PLATFORM DEFAULTS — what the
                // CREATE form renders before the tenant exists (edit mode uses
                // tenantSettings, which carries the tenant's own overrides).
                state.settingsCatalogue = action.payload?.settingsCatalogue || [];
            })

            .addCase(getTenantFeatures.pending, (state) => {
                state.featuresLoading = true;
            })
            .addCase(getTenantFeatures.fulfilled, (state, action) => {
                state.featuresLoading = false;
                state.features = action.payload?.features || null;
                state.tenantServices = action.payload?.services || null;
                state.tenantMeta = {
                    connectionMode: action.payload?.connectionMode || 'connected',
                    subdomain: action.payload?.subdomain || null,
                    tenantType: action.payload?.tenantType || null,
                    isOpenPlatform: !!action.payload?.isOpenPlatform,
                    // What this plan was priced at. null on a tenant created
                    // before pricing existed, or never priced — the UI shows
                    // "not recorded" rather than a fabricated ₹0.
                    planPricing: action.payload?.planPricing || null,
                    // Hospital-level support SLA (one of the SUPPORT_TIERS, or
                    // null = platform default). Only copied when the features
                    // response carries the key at all, so an older backend
                    // that omits it does not override the value the tenant row
                    // (GET /tenants/:id) already supplied to the form.
                    ...(action.payload?.supportLevel !== undefined
                        ? { supportLevel: action.payload.supportLevel || null }
                        : {}),
                };
                // Per-hospital Settings switches (starting values). Separate from
                // `features`, which is entitlement — different question, different
                // owner after creation.
                state.tenantSettings = action.payload?.settings || [];
                state.coreFeatureIds = action.payload?.coreFeatureIds || state.coreFeatureIds;
            })
            .addCase(getTenantFeatures.rejected, (state) => {
                state.featuresLoading = false;
                state.features = null;
                state.tenantServices = null;
                state.tenantMeta = null;
                state.tenantSettings = [];
            })

            .addCase(saveTenantFeatures.pending, (state) => {
                state.featuresSaving = true;
            })
            .addCase(saveTenantFeatures.fulfilled, (state, action) => {
                state.featuresSaving = false;
                if (action.payload?.settings) state.tenantSettings = action.payload.settings;
                state.features = action.payload?.features || state.features;
                state.tenantServices = action.payload?.services || state.tenantServices;
                // The server RECOMPUTES the finals, so the stored plan value is
                // whatever comes back here — not what the form sent.
                if (state.tenantMeta && action.payload?.planPricing !== undefined) {
                    state.tenantMeta.planPricing = action.payload.planPricing || null;
                }
            })
            .addCase(saveTenantFeatures.rejected, (state) => {
                state.featuresSaving = false;
            })

            .addCase(getFeaturePrices.pending, (state) => {
                state.featurePricesLoading = true;
            })
            .addCase(getFeaturePrices.fulfilled, (state, action) => {
                state.featurePricesLoading = false;
                state.featurePrices = Array.isArray(action.payload) ? action.payload : [];
            })
            // Fails soft on purpose: an unpriced catalogue must not stop a
            // tenant being created, and `state.error` is deliberately NOT set —
            // the Tenants list renders ErrorPage off it, so a missing price
            // list would blank out a page that has nothing to do with pricing.
            .addCase(getFeaturePrices.rejected, (state) => {
                state.featurePricesLoading = false;
                state.featurePrices = [];
            })

            .addCase(getMaintenanceAdmin.pending, (state) => {
                state.maintenanceLoading = true;
                state.maintenance = null;
            })
            .addCase(getMaintenanceAdmin.fulfilled, (state, action) => {
                state.maintenanceLoading = false;
                state.maintenance = action.payload?.data || null;
            })
            .addCase(getMaintenanceAdmin.rejected, (state) => {
                state.maintenanceLoading = false;
                state.maintenance = null;
            })

            .addCase(saveMaintenanceAdmin.pending, (state) => { state.maintenanceSaving = true; })
            .addCase(saveMaintenanceAdmin.fulfilled, (state, action) => {
                state.maintenanceSaving = false;
                state.maintenance = action.payload?.data || state.maintenance;
            })
            .addCase(saveMaintenanceAdmin.rejected, (state) => { state.maintenanceSaving = false; })

            .addCase(setMaintenanceAdminStatus.fulfilled, (state, action) => {
                state.maintenance = action.payload?.data || state.maintenance;
            })

            .addCase(listMaintenanceAdmins.pending, (state) => { state.maintenanceListLoading = true; })
            .addCase(listMaintenanceAdmins.fulfilled, (state, action) => {
                state.maintenanceListLoading = false;
                state.maintenanceList = action.payload?.admins || [];
                state.maintenanceTotal = action.payload?.total || 0;
            })
            .addCase(listMaintenanceAdmins.rejected, (state) => { state.maintenanceListLoading = false; });
    },
});

export const { resetTenantState } = tenantSlice.actions;
export default tenantSlice.reducer;
